import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import EmployeeGradeService from "@/modules/EmployeeGrade/services/employeeGrade.service";
import CostCenterService from "@/modules/CostCenter/services/costCenter.service";
import DepartmentService from "@/modules/Department/services/department.service";
import EntityBulkAssignmentService from "../services/entityBulkAssignment.service";
import { mapOrganizationUsersToEmployees } from "../utils/mapOrganizationUsersToEmployees";
import { buildOrganizationUsersParams } from "../utils/buildOrganizationUsersParams";
import { getBulkAssignmentAnalyticsPayload } from "../utils/bulkAssignmentAnalytics";
import {
  AssignmentEntityType,
  BulkAssignmentEntity,
  IEntityBulkAssignmentState,
  IOrganizationUsersResponse,
} from "../types";
import { waitForMs, formatString } from "@/core/utils/helpers";
import analyticsService from "@/analytic.service";
import { BULK_ASSIGNMENT_ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import { ENTITY_TYPE_CONFIG } from "../constants";

const entityBulkAssignmentService = new EntityBulkAssignmentService();
const employeeGradeService = new EmployeeGradeService();
const costCenterService = new CostCenterService();
const departmentService = new DepartmentService();

const showNotification = (type: NOTIFICATION_TYPES, description: string) => {
  notificationService.type = type;
  notificationService.description = description;
  notificationService.triggerNotification();
};

const showErrorNotification = (error: unknown, fallbackMessage: string) => {
  const exception = error as ICoreErrorResponse;
  showNotification(
    NOTIFICATION_TYPES.ERROR,
    exception.message || exception.error || fallbackMessage,
  );
};

const actions: ActionTree<IEntityBulkAssignmentState, StoreState> = {
  async startBulkAssignmentWorkflow(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
    {
      organizationId,
      entityType,
      entity,
    }: {
      organizationId: string;
      entityType: AssignmentEntityType;
      entity: BulkAssignmentEntity;
    },
  ) {
    context.commit("resetBulkAssignmentState");
    context.commit("setWorkflowActive", true);
    context.commit("setCurrentStep", "confirm");
    context.commit("setEntityType", entityType);
    context.commit("setEntity", entity);
    context.commit("setOrganizationId", organizationId);

    analyticsService.logActionEvent(
      BULK_ASSIGNMENT_ANALYTICS_EVENTS.WORKFLOW_STARTED,
      getBulkAssignmentAnalyticsPayload(context.state, {
        employeeCount: entity.employee_count ?? 0,
      }),
    );

    await Promise.all([
      context.dispatch("fetchBulkAssignmentEmployees"),
      context.dispatch("fetchBulkAssignmentOptions"),
    ]);
  },

  proceedBulkAssignmentToAssign(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    context.commit("setCurrentStep", "assign");
    analyticsService.logActionEvent(
      BULK_ASSIGNMENT_ANALYTICS_EVENTS.PROCEED_TO_ASSIGN,
      getBulkAssignmentAnalyticsPayload(context.state, {
        remainingEmployeeCount:
          context.state.employeesPaginationMeta?.total_records ??
          context.state.assignedEmployees.length,
      }),
    );
  },

  backBulkAssignmentStep(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    const { currentStep } = context.state;

    analyticsService.logActionEvent(
      BULK_ASSIGNMENT_ANALYTICS_EVENTS.BACK_STEP,
      getBulkAssignmentAnalyticsPayload(context.state, {
        fromStep: currentStep ?? "",
      }),
    );

    if (currentStep === "assign") {
      context.commit("setLastSuccessfulAssignment", null);
      context.commit("setCurrentStep", "confirm");
      return;
    }

    context.dispatch("cancelBulkAssignmentWorkflow");
  },

  cancelBulkAssignmentWorkflow(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    if (context.state.isWorkflowActive) {
      analyticsService.logActionEvent(
        BULK_ASSIGNMENT_ANALYTICS_EVENTS.WORKFLOW_CANCELLED,
        getBulkAssignmentAnalyticsPayload(context.state),
      );
    }

    context.commit("resetBulkAssignmentState");
  },

  closeBulkAssignmentWorkflow(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    context.commit("resetBulkAssignmentState");
  },

  clearBulkAssignmentLastSuccess(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    context.commit("setLastSuccessfulAssignment", null);
  },

  async fetchBulkAssignmentEmployees(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    const {
      organizationId,
      entityType,
      entity,
      employeesPageNo,
      employeesSearchQuery,
    } = context.state;
    if (!organizationId || !entityType || !entity) return;

    const params = buildOrganizationUsersParams({
      entityType,
      entity,
      pageNo: employeesPageNo,
      searchQuery: employeesSearchQuery,
    });

    try {
      context.commit("setFetchingEmployees", true);
      const response = await entityBulkAssignmentService.getOrganizationUsers(
        context.state.organizationId ?? "",
        params,
      );

      if (response.success && response.status === StatusCodes.OK) {
        const data = response.data as IOrganizationUsersResponse;
        const employees = mapOrganizationUsersToEmployees(data.users ?? []);
        const paginationMeta = data.pagination_meta ?? null;

        if (
          employees.length === 0 &&
          paginationMeta &&
          paginationMeta.total_records > 0 &&
          params.page_no > paginationMeta.total_pages
        ) {
          context.commit("setEmployeesPageNo", paginationMeta.total_pages);
          context.commit("setFetchingEmployees", false);
          await context.dispatch("fetchBulkAssignmentEmployees");
          return;
        }

        context.commit("setAssignedEmployees", employees);
        context.commit("setEmployeesPaginationMeta", paginationMeta);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setAssignedEmployees", []);
      context.commit("setEmployeesPaginationMeta", null);
      analyticsService.logActionEvent(
        BULK_ASSIGNMENT_ANALYTICS_EVENTS.EMPLOYEES_FETCH_FAILED,
        getBulkAssignmentAnalyticsPayload(context.state, {
          pageNo: employeesPageNo,
          searchQuery: employeesSearchQuery,
        }),
      );
      showErrorNotification(
        error,
        NOTIFICATION_MESSAGES.BULK_ASSIGNMENT_EMPLOYEES_FETCH_ERROR,
      );
    } finally {
      context.commit("setFetchingEmployees", false);
    }
  },

  async setBulkAssignmentEmployeesSearchQuery(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
    searchQuery: string,
  ) {
    context.commit("setEmployeesSearchQuery", searchQuery);
    context.commit("setEmployeesPageNo", 1);

    analyticsService.logActionEvent(
      BULK_ASSIGNMENT_ANALYTICS_EVENTS.EMPLOYEES_SEARCH,
      getBulkAssignmentAnalyticsPayload(context.state, {
        searchQuery,
      }),
    );

    await context.dispatch("fetchBulkAssignmentEmployees");
  },

  async setBulkAssignmentEmployeesPageNo(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
    pageNo: number,
  ) {
    context.commit("setEmployeesPageNo", pageNo);

    analyticsService.logActionEvent(
      BULK_ASSIGNMENT_ANALYTICS_EVENTS.EMPLOYEES_PAGE_CHANGE,
      getBulkAssignmentAnalyticsPayload(context.state, {
        pageNo,
      }),
    );

    await context.dispatch("fetchBulkAssignmentEmployees");
  },

  async fetchBulkAssignmentOptions(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
  ) {
    const { organizationId, entityType, entity } = context.state;
    if (!organizationId || !entityType || !entity) return;

    try {
      context.commit("setFetchingAssignmentOptions", true);
      let response: ICoreResponse;
      let options: IEntityBulkAssignmentState["assignmentOptions"] = [];

      if (entityType === "EMPLOYEE_GRADE") {
        response = await employeeGradeService.getEmployeeGrades(
          organizationId,
          true,
        );
        if (!response.success) throw response;
        options = (response.data ?? []).filter(
          (grade: { public_id: string }) =>
            grade.public_id !== entity.public_id,
        );
      } else if (entityType === "COST_CENTER") {
        response = await costCenterService.getCostCenters(organizationId, true);
        if (!response.success) throw response;
        options = (response.data ?? []).filter(
          (center: { public_id: string }) =>
            center.public_id !== entity.public_id,
        );
      } else {
        response = await departmentService.getDepartments(organizationId, true);
        if (!response.success) throw response;
        options = (response.data ?? []).filter(
          (dept: { public_id: string }) => dept.public_id !== entity.public_id,
        );
      }

      context.commit("setAssignmentOptions", options);
    } catch (error: unknown) {
      context.commit("setAssignmentOptions", []);
      analyticsService.logActionEvent(
        BULK_ASSIGNMENT_ANALYTICS_EVENTS.OPTIONS_FETCH_FAILED,
        getBulkAssignmentAnalyticsPayload(context.state),
      );
      showErrorNotification(
        error,
        NOTIFICATION_MESSAGES.BULK_ASSIGNMENT_OPTIONS_FETCH_ERROR,
      );
    } finally {
      context.commit("setFetchingAssignmentOptions", false);
    }
  },

  async bulkAssignEmployees(
    context: ActionContext<IEntityBulkAssignmentState, StoreState>,
    {
      userIds,
      assignmentId,
    }: {
      userIds: string[];
      assignmentId: string;
    },
  ) {
    const { organizationId, entityType } = context.state;
    if (!organizationId || !entityType) return;

    analyticsService.logActionEvent(
      BULK_ASSIGNMENT_ANALYTICS_EVENTS.ASSIGN_SUBMITTED,
      getBulkAssignmentAnalyticsPayload(context.state, {
        selectedEmployeeCount: userIds.length,
        targetAssignmentId: assignmentId,
      }),
    );

    try {
      context.commit("setCurrentStep", "loading");
      context.commit("setAssigning", true);

      const [response] = await Promise.all([
        entityBulkAssignmentService.bulkUpdateAssignment(organizationId, {
          assignment_type: entityType,
          action: "ASSIGN",
          user_ids: userIds,
          assignment_id: assignmentId,
        }),
        waitForMs(1500),
      ]);

      if (response.success && response.status === StatusCodes.OK) {
        await context.dispatch("fetchBulkAssignmentEmployees");

        const remainingCount =
          context.state.employeesPaginationMeta?.total_records ?? 0;
        const entityLabel = ENTITY_TYPE_CONFIG[entityType]?.label ?? "entity";

        analyticsService.logActionEvent(
          BULK_ASSIGNMENT_ANALYTICS_EVENTS.ASSIGN_SUCCESS,
          getBulkAssignmentAnalyticsPayload(context.state, {
            reassignedEmployeeCount: userIds.length,
            remainingEmployeeCount: remainingCount,
            targetAssignmentId: assignmentId,
          }),
        );

        if (remainingCount === 0) {
          context.commit("setCurrentStep", "success");
          analyticsService.logActionEvent(
            BULK_ASSIGNMENT_ANALYTICS_EVENTS.REASSIGNMENT_COMPLETE,
            getBulkAssignmentAnalyticsPayload(context.state),
          );
          showNotification(
            NOTIFICATION_TYPES.SUCCESS,
            formatString(
              NOTIFICATION_MESSAGES.BULK_ASSIGNMENT_ALL_EMPLOYEES_REASSIGNED_SUCCESS,
            ),
          );
        } else {
          const assignmentName =
            context.state.assignmentOptions.find(
              (option) => option.public_id === assignmentId,
            )?.name ?? "";

          context.commit("setLastSuccessfulAssignment", {
            employeeCount: userIds.length,
            assignmentName,
          });
          context.commit("setCurrentStep", "assign");
          showNotification(
            NOTIFICATION_TYPES.SUCCESS,
            response.message ||
              formatString(
                NOTIFICATION_MESSAGES.BULK_ASSIGNMENT_EMPLOYEES_REASSIGNED_SUCCESS,
                userIds.length.toString(),
              ),
          );
        }
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setCurrentStep", "assign");
      analyticsService.logActionEvent(
        BULK_ASSIGNMENT_ANALYTICS_EVENTS.ASSIGN_FAILED,
        getBulkAssignmentAnalyticsPayload(context.state, {
          selectedEmployeeCount: userIds.length,
          targetAssignmentId: assignmentId,
        }),
      );
      showErrorNotification(error, NOTIFICATION_MESSAGES.DEFAULT);
    } finally {
      context.commit("setAssigning", false);
    }
  },
};

export default actions;
